/* eslint-disable @typescript-eslint/no-explicit-any */
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useProfile } from "@/hooks/useProfile";

async function getCustomerId(clinicId: string): Promise<string | null> {
  const { data } = await supabase
    .from("clinics")
    .select("customer_id")
    .eq("id", clinicId)
    .single();
  return (data as any)?.customer_id ?? null;
}

export function useHospitalizations(filters?: { status?: "ativa" | "alta" | "obito" }) {
  const { data: profile } = useProfile();
  const clinicId = profile?.current_clinic_id;

  return useQuery({
    queryKey: ["hospitalizations", clinicId, filters],
    queryFn: async () => {
      if (!clinicId) return [];
      const customerId = await getCustomerId(clinicId);
      if (!customerId) return [];

      let q = (supabase as any)
        .from("hospitalizations")
        .select(`
          id, status, motivo, diagnostico_inicial, entrada_at, previsao_alta_at, alta_at,
          pets ( id, name ),
          hospitalization_bays (
            id, nome,
            hospitalization_sectors ( id, nome )
          )
        `)
        .eq("customer_id", customerId)
        .order("entrada_at", { ascending: false });

      if (filters?.status) {
        q = q.eq("status", filters.status);
      }

      const { data, error } = await q;
      if (error) throw error;
      return (data as any[]) ?? [];
    },
    enabled: !!clinicId,
  });
}

export function useCreateHospitalization() {
  const queryClient = useQueryClient();
  const { data: profile } = useProfile();

  return useMutation({
    mutationFn: async (input: {
      pet_id: string;
      baia_id?: string | null;
      motivo: string;
      diagnostico_inicial?: string | null;
      previsao_alta_at?: string | null;
    }) => {
      const customerId = await getCustomerId(profile!.current_clinic_id!);
      const { data, error } = await (supabase as any)
        .from("hospitalizations")
        .insert({ ...input, customer_id: customerId })
        .select()
        .single();
      if (error) throw error;
      return data as { id: string };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["hospitalizations"] });
      queryClient.invalidateQueries({ queryKey: ["hospitalization-setores"] });
    },
  });
}
